/**
 * Widget Renderer Component
 *
 * Maps widget IDs to their respective components and renders them.
 * Uses React.lazy for new widgets to enable code-splitting.
 * Provides fallback for unimplemented widgets.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React, { Suspense } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { WidgetInstance } from '../types';
import { getWidgetById } from '../widgetRegistry';

// Import existing widget components (eager - already loaded)
import {
  ClockWidget,
  OrderSummaryWidget,
  PickingStatsWidget,
  ShippingStatsWidget,
  KPIGridWidget,
  ActivityFeedWidget,
  OperatorStatusWidget,
  InventoryAlertsWidget,
  HourlyActivityChartWidget,
} from './widgets';

// Lazy-load new widgets for code splitting
const SparklineKPIWidget = React.lazy(() => import('./widgets/SparklineKPIWidget'));
const AnimatedCounterWidget = React.lazy(() => import('./widgets/AnimatedCounterWidget'));
const RadialGaugeWidget = React.lazy(() => import('./widgets/RadialGaugeWidget'));
const GoalTrackerWidget = React.lazy(() => import('./widgets/GoalTrackerWidget'));
const PickAccuracyWidget = React.lazy(() => import('./widgets/PickAccuracyWidget'));
const LeaderboardWidget = React.lazy(() => import('./widgets/LeaderboardWidget'));
const ReplenishmentAlertWidget = React.lazy(() => import('./widgets/ReplenishmentAlertWidget'));
const IncomingDeliveriesWidget = React.lazy(() => import('./widgets/IncomingDeliveriesWidget'));
const BottleneckDetectorWidget = React.lazy(() => import('./widgets/BottleneckDetectorWidget'));
const ComparisonWidget = React.lazy(() => import('./widgets/ComparisonWidget'));
const ShippingCutoffWidget = React.lazy(() => import('./widgets/ShippingCutoffWidget'));
const DailyScorecardWidget = React.lazy(() => import('./widgets/DailyScorecardWidget'));
const ThroughputTimeWidget = React.lazy(() => import('./widgets/ThroughputTimeWidget'));
const OrderFlowWidget = React.lazy(() => import('./widgets/OrderFlowWidget'));
const WorkloadForecastWidget = React.lazy(() => import('./widgets/WorkloadForecastWidget'));
const MiniHeatmapWidget = React.lazy(() => import('./widgets/MiniHeatmapWidget'));
const ZoneUtilizationWidget = React.lazy(() => import('./widgets/ZoneUtilizationWidget'));
const WorkforceBalanceWidget = React.lazy(() => import('./widgets/WorkforceBalanceWidget'));
const SLAMonitorWidget = React.lazy(() => import('./widgets/SLAMonitorWidget'));
const ActivityLogWidget = React.lazy(() => import('./widgets/ActivityLogWidget'));
const RemainingOrdersWidget = React.lazy(() => import('./widgets/RemainingOrdersWidget'));
const ReorderPointAlertsWidget = React.lazy(() => import('./widgets/ReorderPointAlertsWidget'));
const StockValueWidget = React.lazy(() => import('./widgets/StockValueWidget'));
const OrderOverviewWidget = React.lazy(() => import('./widgets/OrderOverviewWidget'));
const CompassReportWidget = React.lazy(() => import('./widgets/CompassReportWidget'));

interface WidgetRendererProps {
  instance: WidgetInstance;
}

/**
 * Map of widget IDs to their components
 */
const WIDGET_COMPONENTS: Record<string, React.FC<{ instance: WidgetInstance }>> = {
  // === SYSTEM ===
  'clock': ClockWidget,

  // === ORDERS ===
  'order-summary': OrderSummaryWidget,
  'orders-pending': OrderSummaryWidget,

  // === PICKING ===
  'picking-stats': PickingStatsWidget,
  'picking-progress': PickingStatsWidget,
  'active-pickers': PickingStatsWidget,

  // === SHIPPING ===
  'shipping-stats': ShippingStatsWidget,
  'ready-to-ship': ShippingStatsWidget,
  'shipping-cutoff': ShippingCutoffWidget,

  // === KPI ===
  'kpi-grid': KPIGridWidget,
  'sparkline-kpi': SparklineKPIWidget,
  'animated-counter': AnimatedCounterWidget,
  'radial-gauge': RadialGaugeWidget,
  'pick-efficiency': RadialGaugeWidget,
  'pick-accuracy': PickAccuracyWidget,
  'on-time-delivery': KPIGridWidget,

  // === ACTIVITY ===
  'activity-feed': ActivityFeedWidget,
  'hourly-activity-chart': HourlyActivityChartWidget,

  // === OPERATORS ===
  'operator-status': OperatorStatusWidget,
  'top-performers': OperatorStatusWidget,

  // === INVENTORY ===
  'inventory-alerts': InventoryAlertsWidget,
  'warehouse-capacity': InventoryAlertsWidget,

  // === GAMIFICATION ===
  'goal-tracker': GoalTrackerWidget,
  'leaderboard': LeaderboardWidget,

  // === ALERTS ===
  'replenishment-alerts': ReplenishmentAlertWidget,
  'bottleneck-detector': BottleneckDetectorWidget,

  // === RECEIVING ===
  'incoming-deliveries': IncomingDeliveriesWidget,

  // === COMPARISON ===
  'comparison': ComparisonWidget,

  // === EXECUTIVE ===
  'daily-scorecard': DailyScorecardWidget,

  // === CHARTS ===
  'throughput-time': ThroughputTimeWidget,
  'order-flow': OrderFlowWidget,
  'workload-forecast': WorkloadForecastWidget,
  'mini-heatmap': MiniHeatmapWidget,

  // === ZONES & WORKFORCE ===
  'zone-utilization': ZoneUtilizationWidget,
  'workforce-balance': WorkforceBalanceWidget,

  // === SLA ===
  'sla-monitor': SLAMonitorWidget,

  // === COLLEAGUE DASHBOARD (Phase 5) ===
  'activity-log': ActivityLogWidget,
  'remaining-orders': RemainingOrdersWidget,
  'reorder-point-alerts': ReorderPointAlertsWidget,
  'stock-value': StockValueWidget,
  'order-overview': OrderOverviewWidget,

  // === COMPASS ===
  'compass-report': CompassReportWidget,
};

/**
 * Loading fallback for lazy-loaded widgets
 */
const WidgetLoadingFallback: React.FC = () => (
  <div className="h-full w-full bg-gray-800 rounded-lg border-2 border-gray-700 flex items-center justify-center">
    <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
  </div>
);

/**
 * Fallback component for unimplemented widgets
 */
const FallbackWidget: React.FC<{ instance: WidgetInstance }> = ({ instance }) => {
  const widgetDef = getWidgetById(instance.widgetId);

  return (
    <div className="h-full w-full bg-gray-800 rounded-lg border-2 border-gray-700 flex flex-col items-center justify-center p-4">
      <AlertCircle className="w-8 h-8 text-gray-500 mb-2" />
      <p className="text-sm text-gray-400 text-center">
        Widget ikke implementert
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {widgetDef?.name || instance.widgetId}
      </p>
    </div>
  );
};

/**
 * Widget Renderer Component
 */
const WidgetRenderer: React.FC<WidgetRendererProps> = ({ instance }) => {
  const WidgetComponent = WIDGET_COMPONENTS[instance.widgetId] || FallbackWidget;

  return (
    <Suspense fallback={<WidgetLoadingFallback />}>
      <WidgetComponent instance={instance} />
    </Suspense>
  );
};

export default WidgetRenderer;
